"use client";

import { useId, useState } from "react";
import Link from "next/link";
import { BookOpen, ChevronRight, ChevronDown, ListTree } from "lucide-react";
import type { Catalog } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * 课程大纲（标注 P1：不要显示全部 14 章全部 Concept，只展开当前章/当前节附近节点）。
 *
 * 层级规则：
 * - 章节默认折叠，只展开当前章
 * - 章内节默认折叠，只展开当前节
 * - 未展开的节只显示节名（可点进节页面），避免 121 个概念全铺开的认知负担
 *
 * - currentConceptId：当前知识点（高亮，自动定位所在章/节）
 * - activeChapterId：指定展开的章（非知识点页传入当前章，如课程/章节/节页）
 */

/** 在目录树里定位概念所在的章/节（纯函数，计算量小无需手写 memo） */
function findActivePosition(catalog: Catalog, conceptId?: string) {
  for (const ch of catalog.chapters) {
    for (const s of ch.sections) {
      if (s.concepts.some((c) => c.id === conceptId)) {
        return { activeChapterId: ch.id, activeSectionId: s.id };
      }
    }
  }
  return {
    activeChapterId: catalog.chapters[0]?.id,
    activeSectionId: catalog.chapters[0]?.sections[0]?.id,
  };
}

function toggleInSet(set: Set<string>, id: string): Set<string> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

export default function CourseOutline({
  catalog,
  courseId,
  currentConceptId,
  activeChapterId,
}: {
  catalog: Catalog;
  courseId: string;
  currentConceptId?: string;
  activeChapterId?: string;
}) {
  const outlineId = useId().replaceAll(":", "");
  const { activeChapterId: derivedChapter, activeSectionId } = findActivePosition(catalog, currentConceptId);

  // activeChapterId 优先（非知识点页传入），否则用推导的
  const chapterToExpand = activeChapterId || derivedChapter;

  // 展开状态 = "自动展开当前章/当前节" + "用户手动覆盖"。
  // 这样软导航（/concepts/a → /concepts/b）切换概念时新章自动展开，
  // 用户手动展开的其他章也不会被收起，且完全不需要 effect 同步。
  const [extraChapters, setExtraChapters] = useState<Set<string>>(new Set());
  const [collapsedChapters, setCollapsedChapters] = useState<Set<string>>(new Set());
  const [extraSections, setExtraSections] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const isChapterOpen = (id: string) =>
    id === chapterToExpand ? !collapsedChapters.has(id) : extraChapters.has(id);
  const isSectionOpen = (id: string) =>
    id === activeSectionId ? !collapsedSections.has(id) : extraSections.has(id);

  function toggleChapter(chapterId: string) {
    if (chapterId === chapterToExpand) setCollapsedChapters((prev) => toggleInSet(prev, chapterId));
    else setExtraChapters((prev) => toggleInSet(prev, chapterId));
  }

  function toggleSection(sectionId: string) {
    if (sectionId === activeSectionId) setCollapsedSections((prev) => toggleInSet(prev, sectionId));
    else setExtraSections((prev) => toggleInSet(prev, sectionId));
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center justify-between px-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold">
          <ListTree className="h-4 w-4 text-primary" />
          课程大纲
        </span>
      </div>

      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1 text-sm">
        {catalog.chapters.map((ch) => {
          const chapterOpen = isChapterOpen(ch.id);
          const total = ch.sections.reduce((a, s) => a + s.concepts.length, 0);
          const mastered = ch.sections.reduce(
            (a, s) => a + s.concepts.filter((c) => c.mastery_status === "mastered").length,
            0
          );
          return (
            <div key={ch.id}>
              <button
                onClick={() => toggleChapter(ch.id)}
                aria-expanded={chapterOpen}
                aria-controls={`${outlineId}-chapter-${ch.id}`}
                className={cn(
                  "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent",
                  chapterOpen && "bg-accent/50"
                )}
              >
                {chapterOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="truncate font-medium">
                  {ch.number}. {ch.title}
                </span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {mastered}/{total}
                </span>
              </button>

              {chapterOpen && (
                <div id={`${outlineId}-chapter-${ch.id}`} className="ml-3 mt-1 space-y-0.5 border-l pl-2">
                  {ch.sections.map((s) => {
                    const sectionOpen = isSectionOpen(s.id);
                    return (
                      <div key={s.id}>
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => toggleSection(s.id)}
                            aria-expanded={sectionOpen}
                            aria-controls={`${outlineId}-section-${s.id}`}
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent"
                            aria-label={sectionOpen ? "收起" : "展开"}
                          >
                            {sectionOpen ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </button>
                          <Link
                            href={`/courses/${courseId}/chapters/${ch.id}/sections/${s.id}`}
                            className="block flex-1 truncate rounded-md px-1 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent"
                          >
                            {s.title}
                          </Link>
                        </div>

                        {sectionOpen && (
                          <div id={`${outlineId}-section-${s.id}`} className="ml-2 space-y-0.5 border-l border-muted pl-2">
                            {s.concepts.map((c) => {
                              const active = c.id === currentConceptId;
                              const status = c.mastery_status;
                              return (
                                <Link
                                  key={c.id}
                                  href={`/concepts/${c.id}`}
                                  aria-current={active ? "page" : undefined}
                                  className={cn(
                                    "flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] transition-colors",
                                    active
                                      ? "bg-primary font-medium text-primary-foreground"
                                      : "text-foreground/80 hover:bg-accent"
                                  )}
                                >
                                  <StatusDot status={status} active={active} />
                                  <span className="truncate">{c.title}</span>
                                  {active && (
                                    <span className="ml-auto shrink-0 rounded-sm bg-primary-foreground/20 px-1 text-[10px]">
                                      当前
                                    </span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="mt-2 space-y-2 border-t pt-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />已掌握
          </span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-warning" />学习中
          </span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-destructive" />薄弱
          </span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />未学
          </span>
        </div>
        <Link
          href="/review"
          className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent"
        >
          <BookOpen className="h-3.5 w-3.5" />
          去复习中心
        </Link>
      </div>
    </div>
  );
}

function StatusDot({ status, active }: { status: string; active?: boolean }) {
  const color =
    status === "mastered"
      ? "bg-success"
      : status === "learning"
        ? "bg-warning"
        : status === "weak"
          ? "bg-destructive"
          : "bg-muted-foreground/30";
  return (
    <span
      aria-hidden="true"
      className={cn(
        "h-1.5 w-1.5 shrink-0 rounded-full",
        color,
        active && "ring-2 ring-primary-foreground/60"
      )}
    />
  );
}
